(() => {
  if (customElements.get('wjd-cart')) return;
  class WjdCart extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.dialog = this.querySelector('dialog');
      this.content = this.querySelector('[data-wjd-content]');
      this.status = this.querySelector('[data-wjd-status]');
      this.root = this.dataset.root;
      if (!this.root.endsWith('/')) this.root += '/';
      this.appliedCodes = [];
      this.busy = false;
      this.setAutomaticOfferState('ready');
      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('change', (event) => this.onChange(event));
      this.addEventListener('input', (event) => this.onInput(event));
      this.addEventListener('toggle', (event) => {
        if (event.target.matches('[data-wjd-details=note]')) this.syncNote();
        if (event.target.matches('[data-wjd-details=discount]')) this.syncDiscountSummary();
        if (event.target.matches('[data-wjd-details]') && event.target.open && !this.busy) this.revealEditor(event.target);
      }, true);
      this.addEventListener('submit', (event) => this.onInternalSubmit(event));
      this.addEventListener('scroll', () => this.syncScrollCue(), true);
      this.addEventListener('load', () => this.syncScrollCue(), true);
      this.addEventListener('focusin', event => { if (event.target.matches('input,textarea')) this.revealEditor(event.target.closest('[data-wjd-details]')); });
      this.onViewportResize = () => { this.syncScrollCue(); const active = document.activeElement; if (this.contains(active)) this.revealEditor(active.closest('[data-wjd-details]')); };
      window.visualViewport?.addEventListener('resize', this.onViewportResize);
      if (window.ResizeObserver) { this.scrollObserver = new ResizeObserver(() => this.syncScrollCue()); this.scrollObserver.observe(this.content); }
      this.dialog?.addEventListener('close', () => this.onClose());
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.policyOpen) { event.preventDefault(); event.stopPropagation(); this.setPolicyView(false); }
      });
      this.preparePolicy();
      this.dialog?.addEventListener('click', (event) => {
        const rect = this.dialog.getBoundingClientRect();
        if (event.target === this.dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom)) this.close();
      });
      document.addEventListener('submit', (event) => this.onProductSubmit(event), true);
      document.addEventListener('click', (event) => this.onCartLink(event), true);
      window.addEventListener('pageshow', (event) => { this.checkoutPending = false; if (event.persisted) this.refresh(); });
      window.addEventListener('focus', () => { if (this.isVisible() && !this.busy) this.refresh(); });
      window.addEventListener('storage', (event) => { if (event.key === 'wjd-cart-updated' && !this.busy && this.isVisible()) this.refresh(); });
      this.syncHeader();
      this.updateCodes({});
      this.syncNote();
      this.loadRecommendations();
      const params = new URLSearchParams(location.search);
      if (params.get('viewcart') === 'true' || params.get('wjd_cart') === 'open') this.open(true);
      else if (this.dataset.mode === 'page' && this.automaticOfferCodes().length) this.refresh();
    }
    disconnectedCallback() { this.scrollObserver?.disconnect(); window.visualViewport?.removeEventListener('resize', this.onViewportResize); }
    syncScrollCue() {
      const scroll = this.querySelector('.wjd-cart__scroll');
      const cue = this.querySelector('[data-wjd-scroll-cue]');
      if (this.scrollObserver && scroll && this.observedScroll !== scroll) { this.scrollObserver.disconnect(); this.scrollObserver.observe(this.content); this.scrollObserver.observe(scroll); const bag = this.querySelector('[data-wjd-bag-view]'); if (bag) this.scrollObserver.observe(bag); this.observedScroll = scroll; }
      const hasMore = !!this.dialog?.open && !!scroll && scroll.scrollHeight > scroll.clientHeight + 2 && scroll.scrollHeight - scroll.clientHeight - scroll.scrollTop > 2;
      this.toggleAttribute('data-scroll-more', hasMore);
      if (cue) cue.hidden = !hasMore;
    }
    revealEditor(details) {
      if (!details?.open || !this.isVisible()) return;
      requestAnimationFrame(() => {
        if (!details.isConnected || !details.open) return;
        const scroll = this.querySelector('.wjd-cart__scroll');
        const form = details.querySelector('form');
        if (!scroll || !form) return;
        if (this.dataset.mode === 'page') { form.scrollIntoView({block:'nearest',behavior:'auto'}); return; }
        const viewport = scroll.getBoundingClientRect();
        const rect = form.getBoundingClientRect();
        const top = viewport.top + 12;
        const bottom = Math.min(viewport.bottom, window.visualViewport ? window.visualViewport.offsetTop + window.visualViewport.height : window.innerHeight) - 20;
        const delta = rect.height > bottom - top ? rect.top - top : rect.bottom > bottom ? rect.bottom - bottom : rect.top < top ? rect.top - top : 0;
        if (delta) scroll.scrollTo({top:scroll.scrollTop + delta,behavior:window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
        this.syncScrollCue();
      });
    }
    isVisible() { return this.dataset.mode === 'page' || Boolean(this.dialog?.open); }
    showStatus(message, error = false, recovery = false) {
      this.status.textContent = message;
      const notice = this.querySelector('[data-wjd-notice]');
      if (notice) { notice.hidden = !message; notice.dataset.error = String(error); }
      this.status.hidden = !message;
      this.status.dataset.error = String(error);
      this.querySelector('[data-wjd-recovery]').hidden = !recovery;
    }
    setBusy(value, message) {
      this.busy = value;
      this.content.setAttribute('aria-busy', String(value));
      this.content.querySelectorAll('button,input,textarea,select').forEach((control) => {
        if (value) {
          if (!('wjdWasDisabled' in control.dataset)) control.dataset.wjdWasDisabled = String(control.disabled);
          control.disabled = true;
        } else if ('wjdWasDisabled' in control.dataset) {
          control.disabled = control.dataset.wjdWasDisabled === 'true';
          delete control.dataset.wjdWasDisabled;
        }
      });
      this.syncNote();
      const giftButton = this.querySelector('[data-wjd-claim-gift]');
      if (!value && giftButton) giftButton.disabled = Boolean(this.giftChecking) || this.querySelector('[data-wjd-gift-state]')?.dataset.present === 'true';
      if (message) this.showStatus(message);
    }
    fieldFeedback(kind, message = '', error = false, reveal = false) {
      const form = this.querySelector('[data-wjd-' + kind + '-form]');
      if (!form) return;
      const feedback = form.querySelector('[data-wjd-field-feedback]');
      const input = form.querySelector('input,textarea');
      if (feedback) { feedback.textContent = message; feedback.hidden = !message; feedback.dataset.error = String(error); }
      input?.setAttribute('aria-invalid', String(error));
      form.dataset.state = error ? 'error' : message ? 'success' : '';
      if (reveal) {
        const details = form.closest('details');
        if (details) details.open = true;
        input?.focus({preventScroll:true});
        this.revealEditor(details);
      }
    }
    syncNote() {
      const form = this.querySelector('[data-wjd-note-form]');
      if (!form) return;
      const note = form.elements.note;
      const button = form.querySelector('button[type=submit]');
      const clean = note.value === note.defaultValue;
      const hasNote = note.defaultValue.trim() !== '';
      const saved = clean && hasNote;
      const saving = this.busy && form.dataset.saving === 'true';
      const details = form.closest('details');
      button.textContent = saving ? form.dataset.savingLabel : form.dataset.saveLabel;
      button.disabled = this.busy || clean;
      button.hidden = clean && !saving;
      const feedback = form.querySelector('[data-wjd-field-feedback]');
      const actions = form.querySelector('[data-wjd-note-actions]');
      if (actions) actions.hidden = button.hidden && (feedback?.hidden !== false || feedback?.dataset.error !== 'true');
      const badge = this.querySelector('[data-wjd-note-badge]');
      if (badge) badge.hidden = !saved;
      const heading = this.querySelector('[data-wjd-note-heading]');
      if (heading) heading.textContent = hasNote ? form.dataset.noteLabel : form.dataset.addLabel;
      const preview = this.querySelector('[data-wjd-note-preview]');
      if (preview) { preview.textContent = note.defaultValue; preview.hidden = !hasNote; }
      const toggle = this.querySelector('[data-wjd-note-toggle]');
      if (toggle) toggle.textContent = details?.open ? form.dataset.closeLabel : hasNote ? form.dataset.editLabel : '+';
    }
    onInput(event) {
      if (event.target.name === 'discount') this.fieldFeedback('discount');
      if (event.target.matches('textarea[name=note]')) { this.fieldFeedback('note'); const actions = event.target.form?.querySelector('[data-wjd-note-actions]'); const wasHidden = actions?.hidden; this.syncNote(); if (wasHidden && !actions?.hidden) this.revealEditor(event.target.closest('details')); }
    }
    open(refresh = true) {
      if (this.dataset.mode === 'page') {
        this.setPolicyView(false, {focus:false});
        const heading = this.querySelector('#WjdCartTitle');
        if (heading) {
          heading.setAttribute('tabindex', '-1');
          heading.focus({preventScroll:true});
          heading.scrollIntoView({block:'start',behavior:window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
        }
      }
      if (this.dialog && !this.dialog.open) {
        this.returnFocus = document.activeElement;
        this.dialog.showModal();
        document.body.classList.add('wjd-cart-is-open');
      }
      this.syncScrollCue();
      if (refresh && !this.busy) this.refresh();
    }
    close() { this.dialog?.close(); }
    onClose() {
      this.setPolicyView(false, {focus:false});
      document.body.classList.remove('wjd-cart-is-open');
      if (this.returnFocus?.isConnected && !this.returnFocus.disabled) this.returnFocus.focus({preventScroll:true});
    }
    preparePolicy() {
      this.querySelectorAll('[data-wjd-policy-document] a').forEach((link) => link.replaceWith(...link.childNodes));
    }
    setPolicyView(open, {focus = true} = {}) {
      const panel = this.querySelector('[data-wjd-policy-view]');
      const bag = this.querySelector('[data-wjd-bag-view]');
      const toggle = this.querySelector('[data-wjd-open-policy]');
      const scroll = this.querySelector('.wjd-cart__scroll');
      if (!panel || !bag) { this.policyOpen = false; return; }
      if (open && !this.policyOpen) this.bagScroll = scroll?.scrollTop || 0;
      this.policyOpen = Boolean(open);
      if (open && focus && !this.busy && this.status?.dataset.error !== 'true') this.showStatus('');
      panel.hidden = !open;
      bag.hidden = Boolean(open);
      toggle?.setAttribute('aria-expanded', String(Boolean(open)));
      const label = toggle?.querySelector('[data-wjd-policy-label]');
      if (label) label.textContent = open ? toggle.dataset.closeLabel : toggle.dataset.openLabel;
      if (scroll) scroll.scrollTop = open ? 0 : this.bagScroll || 0;
      if (focus) {
        const target = open ? panel.querySelector('[data-wjd-policy-heading]') : toggle;
        target?.focus({preventScroll:true});
      }
    }
    onCartLink(event) {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest('a');
      if (link?.hasAttribute('data-wjd-full-cart')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.navigateToCart(link.href);
        return;
      }
      if (!link || link.target === '_blank' || link.hasAttribute('download') || this.contains(link)) return;
      const url = new URL(link.href, location.origin);
      if (url.origin !== location.origin || url.pathname.replace(/\/$/, '') !== this.dataset.cartUrl.replace(/\/$/, '')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.open(true);
    }
    navigateToCart(url) { window.location.assign(url); }
    onProductSubmit(event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const url = new URL(form.action, location.origin);
      if (url.origin !== location.origin || !/\/cart\/add(?:\.js)?\/?$/.test(url.pathname)) return;
      if (event.submitter?.name === 'checkout' || event.submitter?.closest('.shopify-payment-button')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.busy || form.dataset.wjdAdding === 'true') { this.open(false); return; }
      if (!form.reportValidity()) return;
      const body = new FormData(form);
      if (!body.get('id') && ![...body.keys()].some((key) => key.startsWith('items['))) return;
      body.delete('return_to');
      body.set('sections', this.dataset.section);
      body.set('sections_url', location.pathname);
      const submitter = event.submitter || form.querySelector('[type=submit]');
      const wasDisabled = submitter?.disabled;
      form.dataset.wjdAdding = 'true';
      if (submitter) { submitter.disabled = true; submitter.setAttribute('aria-busy', 'true'); }
      this.open(false);
      this.mutate('add', body, this.dataset.adding, this.dataset.added).finally(() => {
        delete form.dataset.wjdAdding;
        if (submitter) { submitter.disabled = Boolean(wasDisabled); submitter.removeAttribute('aria-busy'); }
      });
    }
    onClick(event) {
      if (event.target.closest('[data-wjd-dismiss-status]')) { this.showStatus(''); this.querySelector('[data-wjd-close], .wjd-cart__back')?.focus({preventScroll:true}); return; }
      if (event.target.closest('[data-wjd-scroll-cue]')) { const scroll = this.querySelector('.wjd-cart__scroll'); if (scroll) scroll.scrollTo({top:scroll.scrollHeight,behavior:window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'}); return; }
      if (event.target.closest('[data-wjd-remove-gift]')) {
        if (this.busy) return;
        const line = this.querySelector('[data-wjd-gift-line]');
        if (!line) return;
        try { localStorage.setItem('wjd-gift-declined', 'true'); } catch {}
        this.mutate('change', {id:line.dataset.key,quantity:0}, this.dataset.updating, this.dataset.updated);
        return;
      }
      if (event.target.closest('[data-wjd-claim-gift]')) { this.claimGift(); return; }
      const recommendation = event.target.closest('[data-wjd-add-recommendation]');
      if (recommendation && !this.busy) {
        const card = recommendation.closest('[data-wjd-recommendation]');
        const variant = card.querySelector('select')?.value || recommendation.dataset.variant;
        if (!variant) { card.querySelector('select')?.focus(); return; }
        this.mutate('add', {items:[{id:Number(variant),quantity:1,properties:{_wjd_source:'cart-complementary'}}]}, this.dataset.adding, this.dataset.added);
        return;
      }
      if (event.target.closest('[data-wjd-open-policy]')) { this.setPolicyView(!this.policyOpen); return; }
      if (event.target.closest('[data-wjd-back-to-bag]')) { this.setPolicyView(false); return; }
      if (event.target.closest('[data-wjd-close]')) { this.close(); return; }
      if (event.target.closest('[data-wjd-refresh]')) { this.refresh(); return; }
      const removeCode = event.target.closest('[data-wjd-remove-code]');
      if (removeCode && !this.busy) {
        const codes = this.appliedCodes.filter((code) => code.toUpperCase() !== removeCode.dataset.wjdRemoveCode.toUpperCase());
        this.mutate('update', {discount:codes.join(',')}, '', '', {codes,removedCode:removeCode.dataset.wjdRemoveCode}).then((updated) => {
          if (!updated) return;
          const form = this.querySelector('[data-wjd-discount-form]');
          this.fieldFeedback('discount');
          if (form) form.elements.discount.value = '';
          const announcement = this.querySelector('[data-wjd-discount-announcement]');
          if (announcement) announcement.textContent = form?.dataset.removedLabel || '';
          (this.appliedCodes.length ? form?.closest('details')?.querySelector('summary') : form?.elements.discount)?.focus({preventScroll:true});
        });
        return;
      }
      const remove = event.target.closest('[data-wjd-remove]');
      const stepButton = event.target.closest('[data-wjd-quantity-step]');
      if ((!remove && !stepButton) || this.busy) return;
      const line = (remove || stepButton).closest('[data-wjd-line]');
      const input = line.querySelector('[data-wjd-quantity]');
      let quantity = 0;
      if (!remove) {
        const step = Number(input.step) || 1;
        quantity = Number(input.value) + Number(stepButton.dataset.wjdQuantityStep) * step;
        if (quantity < Number(input.min)) quantity = 0;
        if (input.max && quantity > Number(input.max)) return;
      }
      this.mutate('change', {id:line.dataset.key, quantity}, this.dataset.updating, this.dataset.updated);
    }
    onChange(event) {
      if (!event.target.matches('[data-wjd-quantity]') || this.busy) return;
      const input = event.target;
      if (!input.checkValidity() || !Number.isInteger(Number(input.value))) { input.reportValidity(); return; }
      this.mutate('change', {id:input.closest('[data-wjd-line]').dataset.key, quantity:Number(input.value)}, this.dataset.updating, this.dataset.updated);
    }
    async onInternalSubmit(event) {
      const form = event.target;
      if (form.matches('[data-wjd-discount-form]')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.busy) return;
        const code = form.elements.discount.value.trim();
        if (!code || !form.reportValidity()) return;
        const codes = [...new Set([...this.appliedCodes, code].map((value) => value.toUpperCase()))];
        this.fieldFeedback('discount');
        form.querySelector('button[type=submit]').textContent = form.dataset.applyingLabel;
        await this.mutate('update', {discount:codes.join(',')}, '', '', {codes, submittedCode:code});
      } else if (form.matches('[data-wjd-note-form]')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!this.busy) {
          form.dataset.saving = 'true';
          this.fieldFeedback('note');
          await this.mutate('update', {note:form.elements.note.value}, '', '', {noteSaved:true});
        }
      } else if (form.matches('[data-wjd-checkout-form]')) {
        if (this.busy) { event.preventDefault(); return; }
        this.checkoutPending = true;
        const note = this.querySelector('[name=note]');
        const hiddenNote = form.querySelector('[data-wjd-checkout-note]');
        if (hiddenNote && note) hiddenNote.value = note.value;
        const gift = this.querySelector('[data-wjd-gift-line]');
        if (gift && !form.dataset.giftChecked) {
          event.preventDefault();
          this.setBusy(true);
          const button = form.querySelector('[name=checkout]');
          button.disabled = true;
          button.setAttribute('aria-busy', 'true');
          let keepGift = false;
          try {
            const product = await this.request('products/' + this.querySelector('[data-wjd-gift-state]').dataset.handle + '.js', {timeout:2500});
            keepGift = WjdCart.giftCanCheckout(this.querySelector('[data-wjd-gift-state]')?.dataset, gift.dataset.finalPrice, product.variants);
          } catch { /* The optional gift must never prevent the core purchase. */ }
          if (!keepGift) {
            const update = document.createElement('input');
            update.type = 'hidden'; update.name = 'updates[' + gift.dataset.key + ']'; update.value = '0';
            form.append(update);
          }
          form.dataset.giftChecked = 'true';
          this.setBusy(false);
          button.disabled = false;
          form.requestSubmit(button);
          return;
        }
        const button = form.querySelector('[name=checkout]');
        delete form.dataset.giftChecked;
        button.setAttribute('aria-busy', 'true');
      }
    }
    async request(path, options = {}) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), options.timeout || 18000);
      try {
        const response = await fetch(this.root + path, {...options, cache:'no-store', credentials:'same-origin', signal:controller.signal});
        const data = await response.json();
        if (!response.ok) {
          const message = typeof data.description === 'string' ? data.description : typeof data.message === 'string' ? data.message : this.dataset.networkError;
          const error = new Error(message);
          error.confirmedRejection = true;
          throw error;
        }
        return data;
      } finally { clearTimeout(timer); }
    }
    snapshot() {
      const focused = document.activeElement;
      const line = focused?.closest('[data-wjd-line]');
      return {
        policyOpen:Boolean(this.policyOpen),
        bagScroll:this.bagScroll || 0,
        details:[...this.querySelectorAll('details[open][data-wjd-details]')].map((node) => node.dataset.wjdDetails),
        note:this.querySelector('[name=note]')?.value,
        noteDefault:this.querySelector('[name=note]')?.defaultValue,
        code:this.querySelector('[name=discount]')?.value,
        feedback:['discount','note'].map((kind) => {
          const form = this.querySelector('[data-wjd-' + kind + '-form]');
          const feedback = form?.querySelector('[data-wjd-field-feedback]');
          return {kind,message:feedback?.textContent || '',error:feedback?.dataset.error === 'true',confirmed:form?.dataset.confirmed === 'true'};
        }),
        focusKey:line?.dataset.key,
        focusType:focused?.hasAttribute('data-wjd-quantity') ? '[data-wjd-quantity]' : focused?.hasAttribute('data-wjd-quantity-step') ? '[data-wjd-quantity-step="'+focused.dataset.wjdQuantityStep+'"]' : null,
        focusId:this.contains(focused) ? focused.id : null,
        scroll:this.querySelector('.wjd-cart__scroll')?.scrollTop || 0
      };
    }
    async render(response, state, options = {}) {
      // A note-only save must not replace unrelated cart controls. Compare the
      // actual cart first, so real price/item changes still get a full render.
      if (options.noteSaved && Number.isInteger(response?.item_count) &&
          Array.isArray(response.items) && typeof response.note === 'string' &&
          WjdCart.sectionMatchesCart(this.content, response, {ignoreNote:true})) {
        const note = this.querySelector('textarea[name=note]');
        if (!note) throw new Error(this.dataset.networkError);
        note.defaultValue = response.note;
        note.value = response.note;
        const checkoutNote = this.querySelector('[data-wjd-checkout-note]');
        if (checkoutNote) checkoutNote.value = response.note;
        this.syncGift(response);
        this.syncNote();
        return;
      }
      let html = response?.sections?.[this.dataset.section];
      if (!html) {
        const result = await this.request('?sections=' + encodeURIComponent(this.dataset.section));
        html = result[this.dataset.section];
      }
            let parsed = new DOMParser().parseFromString(html || '', 'text/html');
      let incoming = parsed.querySelector('[data-wjd-content]');
      if (incoming && !WjdCart.sectionMatchesCart(incoming, response)) {
        // Refresh a mismatched snapshot once; never mutate products from stale markup.
        response = await this.request('cart.js');
        const fresh = await this.request('?sections=' + encodeURIComponent(this.dataset.section));
        parsed = new DOMParser().parseFromString(fresh[this.dataset.section] || '', 'text/html');
        incoming = parsed.querySelector('[data-wjd-content]');
        if (!incoming || !WjdCart.sectionMatchesCart(incoming, response)) throw new Error(this.dataset.networkError);
      }
      if (!incoming?.querySelector('[data-wjd-state]')) throw new Error(this.dataset.networkError);
      this.content.replaceChildren(...incoming.childNodes);
      // Newly rendered controls must stay locked until the whole operation settles.
      this.setBusy(this.busy);
      this.preparePolicy();
      this.bagScroll = state.bagScroll || 0;
      this.setPolicyView(Boolean(state.policyOpen), {focus:false});
      for (const name of state.details) {
        const node = [...this.querySelectorAll('[data-wjd-details]')].find((item) => item.dataset.wjdDetails === name);
        if (node) node.open = true;
      }
      const note = this.querySelector('[name=note]');
      if (note && !options.noteSaved && state.note !== state.noteDefault) note.value = state.note ?? '';
      const code = this.querySelector('[name=discount]');
      if (code && state.code && !options.submittedCode) code.value = state.code;
      for (const feedback of state.feedback || []) {
        if ((feedback.kind === 'discount' && options.submittedCode) || (feedback.kind === 'note' && options.noteSaved)) continue;
        this.fieldFeedback(feedback.kind, feedback.message, feedback.error);
        if (feedback.confirmed) {
          const form = this.querySelector('[data-wjd-' + feedback.kind + '-form]');
          if (form) form.dataset.confirmed = 'true';
        }
      }
      const scroll = this.querySelector('.wjd-cart__scroll');
      if (scroll) scroll.scrollTop = state.scroll;
      const line = [...this.querySelectorAll('[data-wjd-line]')].find((node) => node.dataset.key === state.focusKey);
      const focus = state.focusType && line ? line.querySelector(state.focusType) : state.focusId ? this.querySelector('#' + CSS.escape(state.focusId)) : null;
      if (focus) focus.focus({preventScroll:true});
      this.syncHeader();
      this.updateCodes(response);
      this.syncGift(response);
      this.syncNote();
      this.loadRecommendations();
      this.syncScrollCue();
    }
    static isGiftLine(line, variant = '44478676500663') {
      return String(line.variant_id) === String(variant) && (line.properties?._tier === '84c84986-e981-4a40-8964-6b94f62f0349' || line.properties?._wjd_gift === 'travel-box');
    }
    static sectionMatchesCart(content, cart, {ignoreNote = false} = {}) {
      if (!Number.isInteger(cart?.item_count) || !Array.isArray(cart.items)) return true;
      const totals = content.querySelector('[data-wjd-state]')?.dataset;
      if (!totals || Number(totals.count) !== cart.item_count || Number(totals.total) !== cart.total_price) return false;
      const rows = [...content.querySelectorAll('[data-wjd-line]')];
      if (rows.length !== cart.items.length) return false;
      if (!cart.items.every((item) => rows.some((row) => row.dataset.key === item.key && Number(row.dataset.quantity) === item.quantity && Number(row.dataset.finalPrice) === item.final_line_price))) return false;
      const note = content.querySelector('textarea[name=note]');
      return ignoreNote || !note || note.defaultValue === (cart.note || '');
    }
    syncGift(cart) {
      // Add responses contain only added lines; only a full cart can prove absence.
      if (!Number.isInteger(cart?.item_count) || !Array.isArray(cart.items)) return;
      const gift = this.querySelector('[data-wjd-gift-state]');
      if (!gift) return;
      const lines = cart.items.filter((line) => WjdCart.isGiftLine(line, gift.dataset.variant));
      const confirmed = lines.length > 0 && lines.every((line) => line.final_line_price === 0);
      gift.dataset.present = String(lines.length > 0);
      gift.dataset.confirmed = String(confirmed);
      const card = this.querySelector('[data-wjd-gift-card]');
      if (card) card.dataset.confirmed = String(confirmed);
      const removeGift = this.querySelector('[data-wjd-remove-gift]');
      if (removeGift) removeGift.hidden = lines.length === 0;
      const included = this.querySelector('[data-wjd-gift-included]');
      if (included) included.hidden = !confirmed;
      const button = this.querySelector('[data-wjd-claim-gift]');
      if (button) {
        button.hidden = confirmed;
        button.textContent = (confirmed ? gift.dataset.addedLabel : gift.dataset.claimLabel) || button.textContent;
        button.disabled = this.busy || Boolean(this.giftChecking) || lines.length > 0;
      }
      const prompt = this.querySelector('.wjd-cart__gift-prompt');
      const marker = this.querySelector('.wjd-cart__gift-marker');
      const message = confirmed ? this.dataset.giftAdded : gift.dataset.emptyMessage;
      if (prompt && message) prompt.textContent = message;
      if (marker) marker.textContent = confirmed ? '✓' : '✧';
    }
    static giftCanCheckout(state, price, variants) {
      return state?.eligible === 'true' && Number(price) === 0 && Boolean(variants?.some((variant) => String(variant.id) === String(state.variant) && variant.available));
    }
    giftFeedback(message, error = false) {
      const feedback = this.querySelector('[data-wjd-gift-feedback]');
      if (feedback) { feedback.textContent = message; feedback.hidden = !message; feedback.dataset.error = String(error); }
    }
    async claimGift() {
      const gift = this.querySelector('[data-wjd-gift-state]')?.dataset;
      if (this.busy || this.giftChecking || !gift || gift.present === 'true' || gift.eligible !== 'true' || gift.available !== 'true') return;
      try { localStorage.removeItem('wjd-gift-declined'); } catch {}
      this.giftChecking = true;
      this.setBusy(true);
      const button = this.querySelector('[data-wjd-claim-gift]');
      if (button) button.disabled = true;
      this.giftFeedback(this.dataset.giftPending);
      try {
        // Read before adding so a previous uncertain response cannot duplicate a gift.
        let cart = await this.request('cart.js', {timeout:5000});
        if (this.checkoutPending) return;
        cart = await this.prepareGiftPromotion(cart, this.snapshot());
        const existing = cart.items?.some((line) => WjdCart.isGiftLine(line, gift.variant));
        if (existing) {
          const state = this.snapshot();
          await this.render(cart, state);
          await this.reconcileGift(state);
        } else {
          // Transfer the lock synchronously to mutate; no other action can interleave.
          this.setBusy(false);
          await this.mutate('add', {items:[{id:Number(gift.variant),quantity:1,properties:{_attribution:'Rebuy Tiered Progress Bar',_source:'Rebuy',_tier:'84c84986-e981-4a40-8964-6b94f62f0349',_wjd_gift:'travel-box'}}]}, '', '', {gift:true});
        }
      } catch { this.giftFeedback(this.dataset.giftError, true); }
      finally {
        this.setBusy(false);
        this.giftChecking = false;
        this.setBusy(false);
        const currentButton = this.querySelector('[data-wjd-claim-gift]');
        if (currentButton) currentButton.disabled = this.busy || this.querySelector('[data-wjd-gift-state]')?.dataset.present === 'true';
      }
    }
    async reconcileGift(state, options = {}, verified = false) {
      const gift = this.querySelector('[data-wjd-gift-state]')?.dataset;
      const line = this.querySelector('[data-wjd-gift-line]');
      if (!line) return false;
      const keep = gift?.eligible === 'true' && gift.available === 'true' && Number(line.dataset.finalPrice) === 0;
      if (keep && Number(line.dataset.quantity) === 1) return false;
      if (!verified) {
        const current = await this.request('cart.js');
        await this.render(current, state, options);
        return this.reconcileGift(state, options, true);
      }
      const result = await this.request('cart/change.js', {method:'POST',headers:{'Accept':'application/json','Content-Type':'application/json'},body:JSON.stringify({id:line.dataset.key,quantity:keep ? 1 : 0,sections:this.dataset.section,sections_url:location.pathname})});
      await this.render(result, state, options);
      return true;
    }
    async loadRecommendations() {
      const container = this.querySelector('[data-wjd-recommendations]');
      const product = this.querySelector('[data-wjd-line]:not([data-wjd-gift-line])')?.dataset.productId;
      if (!container || !product) return;
      if (this.recommendationProduct !== product) { this.recommendationProduct = product; this.recommendationResults = null; }
      const generation = (this.recommendationGeneration || 0) + 1;
      this.recommendationGeneration = generation;
      try {
        const response = this.recommendationResults || await this.request('recommendations/products.json?product_id=' + encodeURIComponent(product) + '&limit=4&intent=complementary');
        if (generation !== this.recommendationGeneration || !container.isConnected) return;
        this.recommendationResults = response;
        const inCart = [...this.querySelectorAll('[data-wjd-line]')].map((line) => line.dataset.productId);
        const products = (response.products || []).filter((item) => item.available && !inCart.includes(String(item.id))).slice(0,2);
        container.replaceChildren();
        if (!products.length) { container.hidden = true; return; }
        const heading = document.createElement('h3'); heading.textContent = this.dataset.recommendationTitle;
        container.append(heading);
        for (const item of products) {
          const variants = item.variants.filter((variant) => variant.available);
          if (!variants.length) continue;
          const card = document.createElement('div'); card.className = 'wjd-cart__recommendation'; card.dataset.wjdRecommendation = '';
          const image = document.createElement('img'); image.src = item.featured_image; image.alt = ''; image.loading = 'lazy'; image.width = 76; image.height = 76;
          const info = document.createElement('div');
          const title = document.createElement('strong'); title.textContent = item.title; info.append(title);
          const price = document.createElement('span');
          const currency = this.querySelector('[data-wjd-state]')?.dataset.currency || 'USD';
          const format = (amount) => new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency',currency}).format(amount / 100);
          price.textContent = format(variants[0].price); info.append(price);
          const button = document.createElement('button'); button.type = 'button'; button.dataset.wjdAddRecommendation = ''; button.textContent = this.dataset.recommendationAdd; button.dataset.variant = variants.length === 1 ? String(variants[0].id) : '';
          if (variants.length > 1) {
            const select = document.createElement('select'); select.setAttribute('aria-label', this.dataset.chooseOption + ': ' + item.title);
            const empty = document.createElement('option'); empty.value = ''; empty.textContent = this.dataset.chooseOption; select.append(empty);
            for (const variant of variants) { const option = document.createElement('option'); option.value = String(variant.id); option.textContent = variant.title + ' · ' + format(variant.price); select.append(option); }
            select.addEventListener('change', () => { const chosen = variants.find((variant) => String(variant.id) === select.value); price.textContent = chosen ? format(chosen.price) : format(variants[0].price); });
            info.append(select);
          }
          card.append(image, info, button); container.append(card);
        }
        container.hidden = false;
        if (this.busy) this.setBusy(true);
      } catch { /* Recommendations are optional; checkout remains available. */ }
    }
    automaticOfferCodes() {
      return [...new Set((this.dataset.autoOffers || '').split(',').map(code => code.trim().toUpperCase()).filter(code => ['TAKE5','EXTRA5','WATCH500'].includes(code)))];
    }
    automaticOfferPreference(cart) {
      const token = String(cart?.token || '');
      if (!token) return {token:'',mode:'off',owned:[]};
      try {
        const stored = JSON.parse(localStorage.getItem('wjd-auto-offer-choice-v1') || 'null');
        if (stored?.token === token && ['auto','manual','off','gift'].includes(stored.mode) && Array.isArray(stored.owned)) return stored;
      } catch {}
      return this.autoOfferPreference?.token === token ? this.autoOfferPreference : {token,mode:'auto',owned:[]};
    }
    rememberAutomaticOffer(cart, mode, owned = []) {
      if (!this.automaticOfferCodes().length || !cart?.token) return;
      const choice = {token:String(cart.token),mode,owned:[...new Set(owned.map(code => code.toUpperCase()))]};
      this.autoOfferPreference = choice;
      if (mode !== 'auto') this.autoOfferChecked = null;
      try { localStorage.setItem('wjd-auto-offer-choice-v1', JSON.stringify(choice)); } catch {}
    }
    automaticOfferCartCodes(cart) {
      const applications = [...(cart.cart_level_discount_applications || []), ...(cart.items || []).flatMap(item => (item.line_level_discount_allocations || []).map(allocation => allocation.discount_application))];
      return [...new Set([...this.appliedCodes, ...applications.filter(application => application?.type === 'discount_code').map(application => application.title), ...(cart.discount_codes || []).map(entry => entry.code)].filter(Boolean).map(code => code.toUpperCase()))];
    }
    static automaticOfferIdentity(cart) {
      return JSON.stringify([String(cart.token || ''),cart.currency,WjdCart.purchaseIdentity(cart),(cart.items || []).map(item => [item.variant_id,item.quantity,item.original_line_price]).sort((a,b) => String(a[0]).localeCompare(String(b[0])))]);
    }
    async automaticOfferUpdate(codes) {
      return this.request('cart/update.js', {method:'POST',headers:{'Accept':'application/json','Content-Type':'application/json'},body:JSON.stringify({discount:codes.join(','),sections:this.dataset.section,sections_url:location.pathname})});
    }
    async prepareGiftPromotion(cart, state) {
      // Rebuy's server function selects this existing $500 travel-box offer by bar ID.
      // Line attribution alone does not activate it. Preserve the shopper's coupons.
      const barId = '1f7b675f-7be5-417b-afdb-5f1cedb83b01';
      if (cart.attributes?._barId === barId) return cart;
      const updated = await this.request('cart/update.js', {method:'POST',headers:{'Accept':'application/json','Content-Type':'application/json'},body:JSON.stringify({attributes:{_barId:barId},sections:this.dataset.section,sections_url:location.pathname})});
      await this.render(updated, state);
      return updated;
    }
    setAutomaticOfferState(state, cart = null) {
      this.dataset.autoOfferState = state;
      if (new URLSearchParams(location.search).get('wjd_offers_test') !== '1') return;
      const messages = {
        ready:'Ready. Add an item or reopen the bag.', disabled:'Automatic offers are disabled in this theme.',
        checking:'Checking the offers with Shopify.', empty:'Add an item to test automatic savings.',
        manual:'Your manually entered coupon is being kept.', off:'A coupon was removed in this cart. Use a fresh private window for the automatic test.',
        existing:'An existing coupon is being kept.', gift:'Your claimed gift is being kept.', giftcard:'Gift cards have no automatic offer.',
        fallback:'Checking the coupons individually.', unavailable:'No public coupon was accepted for this cart.',
        unchanged:'The existing savings give the lowest checked total.', uncertain:'The request did not finish. Reopen the bag to check again.',
        changed:'Your bag changed during the check. Reopen it to check again.'
      };
      let label = messages[state] || state;
      if (state === 'applied') label = this.automaticOfferActiveCodes(cart).filter(code => this.automaticOfferCodes().includes(code)).join(', ') + ' applied automatically.';
      if (!this.autoOfferTestPanel) {
        const panel = document.createElement('p');
        panel.dataset.wjdOfferTest = '';
        panel.setAttribute('role', 'status');
        panel.setAttribute('aria-live', 'polite');
        panel.style.cssText = 'margin:0;padding:10px 16px;background:#f2f4f0;color:#354035;font:12px/1.5 system-ui;border-bottom:1px solid #e0e5dc;';
        this.content.before(panel);
        this.autoOfferTestPanel = panel;
      }
      this.autoOfferTestPanel.textContent = 'Demo coupon check 2: ' + label;
    }
    automaticOfferActiveCodes(cart) {
      const applications = [...(cart?.cart_level_discount_applications || []), ...(cart?.items || []).flatMap(item => (item.line_level_discount_allocations || []).map(allocation => allocation.discount_application))];
      return [...new Set([...applications.filter(application => application?.type === 'discount_code').map(application => application.title), ...(cart?.discount_codes || []).filter(entry => entry.applicable).map(entry => entry.code)].filter(Boolean).map(code => code.toUpperCase()))];
    }
    automaticOfferStopReason(cart, identity, allowed) {
      if (WjdCart.automaticOfferIdentity(cart) !== identity) return 'changed';
      const mode = this.automaticOfferPreference(cart).mode;
      if (['manual','off'].includes(mode)) return mode;
      if (this.automaticOfferCartCodes(cart).some(code => !allowed.includes(code))) return 'existing';
      if (cart.items.some(item => WjdCart.isGiftLine(item) || (item.final_line_price === 0 && item.original_line_price > 0))) return 'gift';
      return '';
    }
    async automaticOfferFallback(baseline, current, allowed) {
      const identity = WjdCart.automaticOfferIdentity(baseline);
      let bestTotal = baseline.total_price;
      let bestCodes = this.automaticOfferActiveCodes(baseline).filter(code => allowed.includes(code));
      let rejected = false;
      for (const code of allowed) {
        let reason = this.automaticOfferStopReason(current, identity, allowed);
        if (reason) return {cart:current,complete:false,reason};
        try { current = await this.automaticOfferUpdate([code]); }
        catch (error) {
          current = await this.request('cart.js');
          if (!error.confirmedRejection) return {cart:current,complete:false,reason:'uncertain'};
          rejected = true;
        }
        if (!Number.isInteger(current?.item_count) || !Array.isArray(current.items)) current = await this.request('cart.js');
        reason = this.automaticOfferStopReason(current, identity, allowed);
        if (reason) return {cart:current,complete:false,reason};
        if (this.automaticOfferActiveCodes(current).includes(code) && current.total_price < bestTotal) {
          bestTotal = current.total_price;
          bestCodes = [code];
        }
      }
      const active = this.automaticOfferActiveCodes(current).filter(code => allowed.includes(code));
      if (current.total_price > bestTotal || JSON.stringify(active.slice().sort()) !== JSON.stringify(bestCodes.slice().sort())) {
        try { current = await this.automaticOfferUpdate(bestCodes); }
        catch { return {cart:await this.request('cart.js'),complete:false,reason:'uncertain'}; }
        if (!Number.isInteger(current?.item_count) || !Array.isArray(current.items)) current = await this.request('cart.js');
      }
      const reason = this.automaticOfferStopReason(current, identity, allowed);
      return {cart:current,complete:!reason && !rejected && current.total_price <= bestTotal,reason:reason || (current.total_price > bestTotal ? 'uncertain' : '')};
    }
    async applyAutomaticOffers(cart, state, options = {}) {
      const allowed = this.automaticOfferCodes();
      const combinedProductOffers = allowed.includes('EXTRA5') && allowed.includes('WATCH500');
      if (!allowed.length) { this.setAutomaticOfferState('disabled'); return cart; }
      this.setAutomaticOfferState('checking');
      if (!Number.isInteger(cart?.item_count) || !Array.isArray(cart.items) || !WjdCart.sectionMatchesCart(this.content, cart)) {
        cart = await this.request('cart.js');
        await this.render(cart, state, options);
      }
      if (!cart.token) { this.setAutomaticOfferState('uncertain'); return cart; }
      if (!cart.item_count) {
        this.rememberAutomaticOffer(cart, 'auto');
        this.autoOfferChecked = null;
        this.setAutomaticOfferState('empty');
        return cart;
      }
      const choice = this.automaticOfferPreference(cart);
      const codes = this.automaticOfferCartCodes(cart);
      if (['manual','off'].includes(choice.mode)) { this.setAutomaticOfferState(choice.mode); return cart; }
      if (codes.some(code => !choice.owned.includes(code))) { this.setAutomaticOfferState('existing'); return cart; }
      if (cart.items.some(item => WjdCart.isGiftLine(item) || (item.final_line_price === 0 && item.original_line_price > 0))) { this.setAutomaticOfferState('gift'); return cart; }
      if (!cart.items.some(item => !item.gift_card && item.original_line_price > 0)) { this.setAutomaticOfferState('giftcard'); return cart; }
      const identity = WjdCart.automaticOfferIdentity(cart);
      if (identity === this.autoOfferChecked) { this.setAutomaticOfferState(this.automaticOfferActiveCodes(cart).some(code => allowed.includes(code)) ? 'applied' : 'unavailable', cart); return cart; }
      let candidate = cart;
      let uncertain = false;
      let complete = true;
      let reason = '';
      if (combinedProductOffers || !this.autoOfferSingleOnly) {
        try { candidate = await this.automaticOfferUpdate([...new Set([...codes,...allowed])]); }
        catch (error) { uncertain = !error.confirmedRejection; candidate = await this.request('cart.js'); }
        if (!Number.isInteger(candidate?.item_count) || !Array.isArray(candidate.items)) candidate = await this.request('cart.js');
      }
      reason = this.automaticOfferStopReason(candidate, identity, allowed);
      if (reason) complete = false;
      else if (!uncertain && ((!combinedProductOffers && this.autoOfferSingleOnly) || !this.automaticOfferActiveCodes(candidate).some(code => allowed.includes(code)))) {
        this.autoOfferSingleOnly = true;
        this.setAutomaticOfferState('fallback');
        const checked = await this.automaticOfferFallback(cart, candidate, allowed);
        candidate = checked.cart; complete = checked.complete; reason = checked.reason;
      }
      let samePurchase = WjdCart.automaticOfferIdentity(candidate) === identity;
      if (samePurchase && !reason && candidate.total_price > cart.total_price) {
        candidate = await this.automaticOfferUpdate(codes);
        if (!Number.isInteger(candidate?.item_count) || !Array.isArray(candidate.items)) candidate = await this.request('cart.js');
        samePurchase = WjdCart.automaticOfferIdentity(candidate) === identity;
        complete = false;
        reason = 'uncertain';
      }
      const active = this.automaticOfferActiveCodes(candidate).filter(code => allowed.includes(code));
      if (uncertain) { complete = false; reason = 'uncertain'; }
      await this.render(candidate, state, options);
      if (samePurchase && !reason) this.rememberAutomaticOffer(candidate, 'auto', allowed);
      this.autoOfferChecked = samePurchase && complete && !reason ? identity : null;
      this.setAutomaticOfferState(reason || (active.length ? 'applied' : candidate.total_price < cart.original_total_price ? 'unchanged' : 'unavailable'), candidate);
      return candidate;
    }
    updateCodes(cart) {
      const applications = [...(cart?.cart_level_discount_applications || []), ...(cart?.items || []).flatMap((item) => (item.line_level_discount_allocations || []).map((allocation) => allocation.discount_application))];
      const domCodes = [...this.querySelectorAll('[data-wjd-discount-type=discount_code]')].map((node) => node.dataset.wjdAppliedTitle);
      const reportedCodes = (cart?.discount_codes || []).filter((code) => code.applicable).map((code) => code.code);
      this.appliedCodes = [...new Set([...domCodes, ...applications.filter((application) => application.type === 'discount_code').map((application) => application.title), ...reportedCodes])];
      const list = this.querySelector('[data-wjd-code-list]');
      if (!list) return;
      list.replaceChildren();
      for (const code of this.appliedCodes) {
        const row = document.createElement('div');
        row.className = 'wjd-cart__code-row';
        const name = document.createElement('strong'); name.textContent = code;
        const status = document.createElement('span'); status.className = 'wjd-cart__code-status';
        const form = this.querySelector('[data-wjd-discount-form]');
        status.textContent = '✓ ' + form.dataset.activeLabel;
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.wjdRemoveCode = code;
        button.textContent = form.dataset.removeText;
        button.setAttribute('aria-label', form.dataset.removeLabel?.replace('[code]', code) || code);
        row.append(name, status, button);
        list.append(row);
      }
      this.syncDiscountSummary();
    }
    syncDiscountSummary() {
      const form = this.querySelector('[data-wjd-discount-form]');
      const details = form?.closest('details');
      if (!form || !details) return;
      const codes = this.appliedCodes || [];
      const applied = codes.length > 0;
      const title = details.querySelector('[data-wjd-discount-heading]');
      const check = details.querySelector('[data-wjd-discount-check]');
      const preview = details.querySelector('[data-wjd-discount-preview]');
      const toggle = details.querySelector('[data-wjd-discount-toggle]');
      const input = form.elements.discount;
      if (title) title.textContent = applied ? codes.length > 1 ? form.dataset.pluralLabel.replace('[count]', String(codes.length)) : form.dataset.appliedLabel : form.dataset.addLabel;
      if (check) check.hidden = !applied;
      if (toggle) toggle.textContent = details.open ? form.dataset.closeLabel : applied ? form.dataset.editLabel : '+';
      details.dataset.applied = String(applied);
      if (input) input.placeholder = applied ? form.dataset.anotherLabel : form.dataset.codeLabel;
      if (preview) {
        const amounts = new Map();
        this.querySelectorAll('[data-wjd-discount-type=discount_code][data-wjd-discount-amount]').forEach((node) => {
          const code = node.dataset.wjdAppliedTitle.toUpperCase();
          amounts.set(code, (amounts.get(code) || 0) + Number(node.dataset.wjdDiscountAmount || 0));
        });
        const currency = this.querySelector('[data-wjd-state]')?.dataset.currency || 'USD';
        const money = new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency',currency});
        preview.textContent = codes.map((code) => {
          const amount = amounts.get(code.toUpperCase());
          return amount > 0 ? code + ' · −' + money.format(amount / 100) : code;
        }).join('; ');
        preview.hidden = !applied;
      }
    }
    syncHeader() {
      const state = this.querySelector('[data-wjd-state]');
      if (!state) return;
      const count = Number(state.dataset.count);
      this.querySelector('[data-wjd-count]').textContent = String(count) + ' ' + (count === 1 ? this.dataset.itemLabel : this.dataset.itemsLabel);
      const link = document.querySelector('#cart-icon-bubble');
      if (!link) return;
      let bubble = link.querySelector('.cart-count-bubble');
      if (!count) { bubble?.remove(); return; }
      if (!bubble) {
        bubble = document.createElement('div');
        bubble.className = 'cart-count-bubble';
        const text = document.createElement('span');
        text.setAttribute('aria-hidden', 'true');
        bubble.append(text);
        link.append(bubble);
      }
      const visible = bubble.querySelector('[aria-hidden=true]') || bubble.querySelector('span');
      if (visible) visible.textContent = String(count);
      const sr = bubble.querySelector('.visually-hidden');
      if (sr) sr.textContent = String(count);
    }
    async refresh() {
      if (this.busy) return false;
      const state = this.snapshot();
      const before = {
        claimed:this.querySelector('[data-wjd-gift-state]')?.dataset.confirmed === 'true' && this.querySelector('[data-wjd-gift-state]')?.dataset.eligible === 'true',
        currency:this.querySelector('[data-wjd-state]')?.dataset.currency,
        lines:[...this.querySelectorAll('[data-wjd-line]:not([data-wjd-gift-line])')].map(line => ({key:line.dataset.key,quantity:Number(line.dataset.quantity),price:Number(line.dataset.finalPrice)}))
      };
      this.setBusy(true);
      try {
        let cart = await this.request('cart.js');
        cart = await this.retainGiftAfterNote(cart, before);
        const paidGift = cart.items?.some(line => WjdCart.isGiftLine(line) && line.final_line_price !== 0);
        if (paidGift) cart = await this.request('cart.js');
        await this.render(cart, state);
        cart = await this.applyAutomaticOffers(cart, state);
        // Passive window-focus refreshes must never remove a claimed gift.
        // Item changes reconcile eligibility; checkout independently checks it.
        this.showStatus('');
        return true;
      } catch (error) {
        this.showStatus(this.dataset.networkError, true, true);
        return false;
      } finally { this.setBusy(false); }
    }
    static purchaseIdentity(cart) {
      return JSON.stringify((cart.items || []).filter(line => !WjdCart.isGiftLine(line)).map(line => JSON.stringify([line.variant_id,line.quantity,line.selling_plan_allocation?.selling_plan?.id || null,Object.entries(line.properties || {}).sort(([a],[b]) => a.localeCompare(b))])).sort());
    }
    async retainGiftAfterNote(cart, before, coupon = null) {
      if (!before.claimed || before.recoveryAttempted || !Array.isArray(cart?.items)) return cart;
      try { if (localStorage.getItem('wjd-gift-declined') === 'true') return cart; } catch {}
      const hasGift = value => value.items?.some(line => WjdCart.isGiftLine(line));
      if (hasGift(cart)) return cart;
      // Do not restore a gift if merchandise changed in another tab.
      const samePurchase = value => {
        if (coupon) return value.currency === before.currency && WjdCart.purchaseIdentity(value) === before.purchase;
        const lines = value.items?.filter(line => !WjdCart.isGiftLine(line));
        return value.currency === before.currency && lines?.length === before.lines.length &&
          lines.every(line => before.lines.some(old => old.key === line.key && old.quantity === line.quantity && old.price === line.final_line_price));
      };
      if (!samePurchase(cart)) return cart;
      // A missing line in an update response alone is not enough to add again.
      const current = await this.request('cart.js');
      if (hasGift(current) || !samePurchase(current)) return current;
      if (coupon) {
        await this.render(current, coupon.state, coupon.options);
        const eligibility = this.querySelector('[data-wjd-gift-state]')?.dataset;
        if (eligibility?.eligible !== 'true' || eligibility.available !== 'true') return current;
      }
      before.recoveryAttempted = true;
      await this.prepareGiftPromotion(current, this.snapshot());
      await this.request('cart/add.js', {method:'POST',headers:{'Accept':'application/json','Content-Type':'application/json'},body:JSON.stringify({items:[{id:44478676500663,quantity:1,properties:{_attribution:'Rebuy Tiered Progress Bar',_source:'Rebuy',_tier:'84c84986-e981-4a40-8964-6b94f62f0349',_wjd_gift:'travel-box'}}]})});
      // Never retry this write. Confirm the server's final cart, including price.
      const restored = await this.request('cart.js');
      if (!restored.items?.some(line => WjdCart.isGiftLine(line) && line.final_line_price === 0)) {
        await this.render(restored, this.snapshot());
        await this.reconcileGift(this.snapshot());
        throw new Error(this.dataset.giftError);
      }
      return restored;
    }
    async mutate(action, data, pending, success, options = {}) {
      if (this.busy) return false;
      const state = this.snapshot();
      const couponUpdate = action === 'update' && Object.prototype.hasOwnProperty.call(data, 'discount');
      const giftBefore = options.noteSaved || couponUpdate ? {
        claimed:this.querySelector('[data-wjd-gift-state]')?.dataset.confirmed === 'true' && this.querySelector('[data-wjd-gift-state]')?.dataset.eligible === 'true',
        currency:this.querySelector('[data-wjd-state]')?.dataset.currency,
        lines:[...this.querySelectorAll('[data-wjd-line]:not([data-wjd-gift-line])')].map(line => ({key:line.dataset.key,quantity:Number(line.dataset.quantity),price:Number(line.dataset.finalPrice)}))
      } : null;
      this.setBusy(true, pending);
      try {
        if (couponUpdate && giftBefore.claimed) {
          const baseline = await this.request('cart.js');
          // Preserve the buyer's confirmed claim when only the gift disappeared.
          // A different purchase in another tab must not inherit that claim.
          const merchandise = baseline.items?.filter(line => !WjdCart.isGiftLine(line));
          giftBefore.claimed = baseline.currency === giftBefore.currency && merchandise?.length === giftBefore.lines.length &&
            merchandise.every(line => giftBefore.lines.some(old => old.key === line.key && old.quantity === line.quantity && old.price === line.final_line_price));
          giftBefore.purchase = WjdCart.purchaseIdentity(baseline);
          giftBefore.currency = baseline.currency;
        }
        const isForm = data instanceof FormData;
        if (!isForm && !options.noteSaved) data = {...data, sections:this.dataset.section, sections_url:location.pathname};
        let result = await this.request('cart/' + action + '.js', {method:'POST',headers:isForm ? {'Accept':'application/json'} : {'Accept':'application/json','Content-Type':'application/json'},body:isForm ? data : JSON.stringify(data)});
        if (options.noteSaved || couponUpdate) result = await this.retainGiftAfterNote(result, giftBefore, couponUpdate ? {state,options} : null);
        await this.render(result, state, options);
        // Rendering can obtain a newer cart than the mutation response. Check that
        // final state too, under the same lock and with at most one recovery write.
        if (couponUpdate && giftBefore.claimed && this.querySelector('[data-wjd-gift-state]')?.dataset.present !== 'true') {
          result = await this.request('cart.js');
          result = await this.retainGiftAfterNote(result, giftBefore, {state,options});
          await this.render(result, state, options);
        }
        // A rejected code is feedback, not consent to remove an already claimed gift.
        // Re-read a priced gift once before rendering; checkout still verifies eligibility.
        const rejectedCode = options.submittedCode && !this.appliedCodes.some(code => code.toUpperCase() === options.submittedCode.toUpperCase());
        if (rejectedCode && result.items?.some(line => WjdCart.isGiftLine(line) && line.final_line_price !== 0)) {
          result = await this.request('cart.js');
          await this.render(result, state, options);
        }
        const giftRemoved = options.noteSaved || rejectedCode ? false : await this.reconcileGift(state, options);
        if (!couponUpdate && !options.noteSaved && !options.gift) result = await this.applyAutomaticOffers(result, state, options);
        if (couponUpdate && options.removedCode) this.rememberAutomaticOffer(result, 'off');
        if (action === 'add') {
          const modal = document.querySelector('quick-add-modal[open]');
          if (modal && typeof modal.hide === 'function') modal.hide(true);
        }
        if (options.gift) {
          this.showStatus('');
          const confirmed = this.querySelector('[data-wjd-gift-state]')?.dataset.confirmed === 'true';
          this.giftFeedback(confirmed && !giftRemoved ? '' : this.dataset.giftError, !confirmed || giftRemoved);
        } else if (options.submittedCode) {
          const accepted = this.appliedCodes.some((code) => code.toUpperCase() === options.submittedCode.toUpperCase());
          this.showStatus('');
          if (accepted) {
            this.rememberAutomaticOffer(result, 'manual');
            this.fieldFeedback('discount');
            const form = this.querySelector('[data-wjd-discount-form]');
            if (form) {
              form.elements.discount.value = '';
              const details = form.closest('details');
              if (details) { details.open = false; details.querySelector('summary')?.focus({preventScroll:true}); }
            }
            const announcement = this.querySelector('[data-wjd-discount-announcement]');
            if (announcement) announcement.textContent = this.dataset.codeApplied;
            this.syncDiscountSummary();
          } else {
            this.fieldFeedback('discount', this.dataset.checkCode, true, true);
            const input = this.querySelector('[name=discount]');
            if (input) { input.value = options.submittedCode; input.setAttribute('aria-invalid', 'true'); }
          }
        } else if (options.noteSaved) {
          this.showStatus('');
          const form = this.querySelector('[data-wjd-note-form]');
          this.fieldFeedback('note');
          if (form) {
            form.dataset.confirmed = 'true';
            const announcement = form.querySelector('[data-wjd-note-announcement]');
            if (announcement) announcement.textContent = this.dataset.noteSaved;
            const details = form.closest('details');
            if (details) { details.open = false; details.querySelector('summary')?.focus({preventScroll:true}); }
          }
        } else this.showStatus(success);
        try { localStorage.setItem('wjd-cart-updated', String(Date.now())); } catch {}
        document.dispatchEvent(new CustomEvent('wjd:cart:updated', {detail:{action,itemCount:Number(this.querySelector('[data-wjd-state]')?.dataset.count || 0)}}));
        return true;
      } catch (error) {
        const message = error.confirmedRejection ? error.message : this.dataset.networkError;
        if (options.gift) {
          this.showStatus('');
          this.giftFeedback(this.dataset.giftError, true);
        } else if (options.submittedCode || options.noteSaved) {
          this.showStatus('');
          this.fieldFeedback(options.submittedCode ? 'discount' : 'note', message, true, true);
        } else this.showStatus(message, true, true);
        return false;
      } finally {
        const noteForm = this.querySelector('[data-wjd-note-form]');
        if (noteForm) delete noteForm.dataset.saving;
        const discountForm = this.querySelector('[data-wjd-discount-form]');
        if (discountForm) discountForm.querySelector('button[type=submit]').textContent = discountForm.dataset.applyLabel;
        this.setBusy(false);
      }
    }
  }
  customElements.define('wjd-cart', WjdCart);
})();


